import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Brain,
  Building2,
  Calendar,
  CheckCircle2,
  Coins,
  ExternalLink,
  ListChecks,
  Loader2,
  MapPin,
  RefreshCw,
  Trophy,
  XCircle,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { mercadoPublicoUrl } from "@/lib/licitaciones";
import { ScoreRing } from "@/components/intelligence/ScoreRing";
import { RecommendationBadge } from "@/components/intelligence/RecommendationBadge";
import { MatchFactorBar } from "@/components/intelligence/MatchFactorBar";
import { CompanyVsTenderTable } from "@/components/intelligence/CompanyVsTenderTable";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { VerdictPanel } from "@/components/intelligence/VerdictPanel";
import { RiesgoFavoritoCard } from "@/components/oc/RiesgoFavoritoCard";
import { RulesPanel } from "@/components/intelligence/RulesPanel";
import { RecommendationsList } from "@/components/intelligence/RecommendationsList";
import { AgentConsole } from "@/components/intelligence/AgentConsole";
import { levelLabel, nivelTone } from "@/lib/intelligence/scoring";
import { diasRestantes } from "@/lib/mock-data";
import { montoConEquivalente } from "@/lib/monto";
import { useIndicadores } from "@/hooks/use-indicadores";
import { cn } from "@/lib/utils";
import { companyProfileService } from "@/services";
import { opportunitiesService } from "@/services/opportunities.service";
import { decisionsService } from "@/services/decisions.service";
import { DecisionDialog } from "@/components/decisiones/DecisionDialog";
import type { DecisionBucket } from "@/types";

function NotFoundOpp() {
  return (
    <AppShell title="Oportunidad no encontrada">
      <EmptyState
        title="No encontramos la oportunidad"
        description="Es posible que haya sido archivada o eliminada del motor."
        action={
          <Link to="/oportunidades" className="text-sm text-primary hover:underline">
            Volver al panel
          </Link>
        }
      />
    </AppShell>
  );
}

function ErrorOpp({ reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  return (
    <AppShell title="Algo falló">
      <EmptyState
        icon={AlertTriangle}
        title="No pudimos cargar la oportunidad"
        action={
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="text-sm text-primary hover:underline"
          >
            Reintentar
          </button>
        }
      />
    </AppShell>
  );
}

export const Route = createFileRoute("/oportunidades/$id")({
  head: () => ({
    meta: [
      { title: "Detalle de oportunidad — LicitaPro" },
      {
        name: "description",
        content:
          "Análisis IA completo: veredicto, factores de matching, reglas internas y recomendaciones.",
      },
      { property: "og:title", content: "Detalle de oportunidad — LicitaPro" },
      { property: "og:description", content: "Análisis completo del copiloto de adjudicaciones." },
    ],
  }),
  notFoundComponent: NotFoundOpp,
  errorComponent: ErrorOpp,
  component: OportunidadDetalle,
});

const mensajeError = (err: unknown) => {
  const texto = err instanceof Error ? err.message : String(err);
  if (texto.includes("429"))
    return "El proveedor de IA está limitando las solicitudes. Reintenta en unos minutos.";
  if (texto.includes("402")) return "Tu proveedor de IA reporta saldo agotado. Revisa tu cuenta.";
  return texto;
};

export function OportunidadDetalle() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [enCurso, setEnCurso] = useState<string[]>([]);
  const [pendiente, setPendiente] = useState<{ nombre: string; bucket: DecisionBucket } | null>(
    null,
  );

  const oportunidad = useQuery({
    queryKey: ["oportunidad", id],
    queryFn: () => opportunitiesService.byId(id),
    retry: false,
  });
  const perfil = useQuery({ queryKey: ["company-profile"], queryFn: companyProfileService.get });
  // UF y UTM del día, para mostrar el equivalente en pesos junto al monto.
  const { indicadores } = useIndicadores();
  const analisis = useQuery({
    queryKey: ["oportunidad-analisis", id],
    queryFn: () => opportunitiesService.analisis(id),
    retry: false,
  });

  const decidir_ = useMutation({
    mutationFn: ({ bucket, motivo }: { bucket: DecisionBucket; motivo: string }) =>
      decisionsService.decide(id, bucket, motivo),
    onSuccess: () => {
      toast.success("Decisión registrada");
      setPendiente(null);
      void qc.invalidateQueries({ queryKey: ["decisions"] });
      void qc.invalidateQueries({ queryKey: ["crm"] });
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "No se pudo registrar la decisión",
      ),
  });

  const reevaluar = useMutation({
    mutationFn: () => opportunitiesService.evaluar(id),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["oportunidad", id] });
      if (res.evaluadas === 0 && res.errores > 0) {
        toast.warning("La IA no pudo evaluar: se mantuvo el análisis base", {
          description: res.error ?? "El modelo devolvió una respuesta inválida.",
        });
        return;
      }
      toast.success("Evaluación IA actualizada");
    },
    onError: (err) => toast.error("No se pudo evaluar", { description: mensajeError(err) }),
  });


  async function correrAgente(agenteId: string) {
    setEnCurso((v) => [...v, agenteId]);
    setErrores((e) => ({ ...e, [agenteId]: "" }));
    try {
      await opportunitiesService.analizar(id, agenteId);
      await qc.invalidateQueries({ queryKey: ["oportunidad-analisis", id] });
      toast.success("Análisis generado");
    } catch (err) {
      const msg = mensajeError(err);
      setErrores((e) => ({ ...e, [agenteId]: msg }));
      toast.error("El agente no pudo analizar", { description: msg });
    } finally {
      setEnCurso((v) => v.filter((x) => x !== agenteId));
    }
  }

  async function correrTodos() {
    const activos = (analisis.data ?? []).filter((a) => a.activo).map((a) => a.agenteId);
    for (const agenteId of activos) await correrAgente(agenteId);
  }

  if (oportunidad.isLoading) return <DetalleSkeleton />;
  if (oportunidad.isError || !oportunidad.data) return <NotFoundOpp />;

  const { licitacion: lic, scoring, origen, reglasEvaluadas, evaluadoAt } = oportunidad.data;
  const dias = diasRestantes(lic.fechaCierre);
  const sinMotor = (analisis.error as Error | null)?.message?.includes("no está configurado");

  /**
   * Abre el diálogo de decisión. Antes estos botones llamaban a
   * `learningService.track()`, que escribía una señal en localStorage que
   * nadie leía nunca: el módulo `/learning` no existe en el backend y
   * `signals()`/`insights()` no tenían consumidores. Ahora usan el Decision
   * Center, que sí persiste (DecisionEvento, con score y recomendación de la
   * IA al momento de decidir) y mueve el pipeline del CRM.
   */
  function decidir(bucket: DecisionBucket) {
    setPendiente({ nombre: lic.nombre, bucket });
  }

  return (
    <AppShell
      title={lic.nombre}
      subtitle={`${lic.organismo} · ${lic.codigo}`}
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => reevaluar.mutate()}
            disabled={reevaluar.isPending}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-gradient-primary text-primary-foreground font-medium shadow-glow hover:opacity-90 disabled:opacity-60 transition"
          >
            {reevaluar.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            Re-analizar con IA
          </button>
          {/* Ficha oficial en Mercado Público: desde acá se descargan las bases
              y se publican las consultas, que es lo que el análisis no cubre. */}
          <a
            href={mercadoPublicoUrl(lic.codigo)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
          >
            <ExternalLink className="size-3.5" /> Publicación original
          </a>
          <Link
            to="/oportunidades"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
          >
            <ArrowLeft className="size-3.5" /> Volver
          </Link>
        </div>
      }
    >
      {sinMotor && (
        <div className="mb-4 rounded-xl border border-warning/30 bg-warning/5 p-4 flex flex-wrap items-center gap-3">
          <AlertTriangle className="size-4 text-warning" />
          <p className="text-sm text-foreground flex-1 min-w-[240px]">
            El motor IA no está configurado. Estás viendo el análisis base calculado con la fórmula
            de la plataforma.
          </p>
          <Link to="/ia" className="text-sm text-primary hover:underline">
            Configurar Motor IA
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        {/* Score */}
        <div className="rounded-xl border border-border bg-card p-5 shadow-elegant">
          <div className="flex items-center gap-4 mb-4">
            <ScoreRing score={scoring.score} size={96} stroke={8} label="MATCH" />
            <div>
              <RecommendationBadge value={scoring.recommendation} size="lg" />
              <div className="text-xs text-muted-foreground mt-2">Probabilidad de adjudicación</div>
              <div className="text-2xl font-display font-semibold text-foreground tabular-nums">
                {scoring.probabilidad}%
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-4 border-t border-border">
            <Tag
              label="Riesgo"
              value={levelLabel(scoring.riesgo)}
              tone={nivelTone(scoring.riesgo)}
            />
            <Tag
              label="Competencia"
              value={levelLabel(scoring.competencia)}
              tone={scoring.competencia === "alto" ? "warning" : "muted"}
            />
            <Tag label="Complejidad" value={levelLabel(scoring.complejidad)} tone="muted" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            {origen === "ia"
              ? `Análisis IA${oportunidad.data.modelo ? ` · ${oportunidad.data.modelo}` : ""}${
                  evaluadoAt ? ` · ${new Date(evaluadoAt).toLocaleString("es-CL")}` : ""
                }`
              : "Análisis base (fórmula de la plataforma)"}
          </p>
        </div>

        {/* Datos clave */}
        <div className="rounded-xl border border-border bg-card p-5 shadow-elegant">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2">
            <Brain className="size-4 text-primary" /> Datos clave
          </h3>
          <div className="space-y-2.5 text-sm">
            {/* El monto se muestra en la unidad en que se publicó, con el peso
                como referencia. `formatCLP` le ponía signo peso a cualquier
                número, así que un monto en UF se leía multiplicado por 39.000. */}
            <DataRow
              icon={Coins}
              label="Monto estimado"
              value={montoConEquivalente(lic.montoEstimado, lic.moneda, indicadores)}
            />
            <DataRow
              icon={Calendar}
              label="Cierre"
              value={`${new Date(lic.fechaCierre).toLocaleDateString("es-CL")} · ${dias > 0 ? `${dias} días` : "cerrada"}`}
              accent={dias < 3 ? "destructive" : dias < 7 ? "warning" : undefined}
            />
            <DataRow icon={MapPin} label="Región" value={lic.region} />
            <DataRow icon={Building2} label="Modalidad" value={lic.modalidad} />
          </div>
        </div>

        {/* Acciones */}
        <div className="rounded-xl border border-border bg-card p-5 shadow-elegant">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2">
            <ListChecks className="size-4 text-primary" /> Acciones del copiloto
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => decidir("postular")}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-gradient-primary text-primary-foreground text-sm font-medium px-3 py-2 shadow-glow hover:opacity-90 transition"
            >
              <Trophy className="size-4" /> Marcar para postular
            </button>
            <button
              onClick={() => decidir("revisar")}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-border text-foreground text-sm font-medium px-3 py-2 hover:bg-muted transition"
            >
              Mover a revisar
            </button>
            <button
              onClick={() => decidir("no_postular")}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-destructive/30 text-destructive text-sm font-medium px-3 py-2 hover:bg-destructive/10 transition"
            >
              Descartar oportunidad
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Cada acción entrena al motor para mejorar tus próximas recomendaciones.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <VerdictPanel
            recommendation={scoring.recommendation}
            texto={oportunidad.data.veredictoTexto}
            origen={origen}
          />

          <RiesgoFavoritoCard organismo={lic.organismo} />

          {/* Pro / Contra */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-success/30 bg-success/5 p-5">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-3 text-success">
                <CheckCircle2 className="size-4" /> Por qué POSTULAR
              </h3>
              {scoring.proRazones.length ? (
                <ul className="space-y-2 text-sm text-foreground">
                  {scoring.proRazones.map((r) => (
                    <li key={r} className="flex gap-2">
                      <CheckCircle2 className="size-4 text-success mt-0.5 shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  El motor no encontró razones fuertes a favor.
                </p>
              )}
            </div>
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-3 text-destructive">
                <XCircle className="size-4" /> Por qué NO POSTULAR
              </h3>
              {scoring.contraRazones.length ? (
                <ul className="space-y-2 text-sm text-foreground">
                  {scoring.contraRazones.map((r) => (
                    <li key={r} className="flex gap-2">
                      <XCircle className="size-4 text-destructive mt-0.5 shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No detectamos bloqueos relevantes.</p>
              )}
            </div>
          </div>

          {/* Factores */}
          <section className="rounded-xl border border-border bg-card p-5 shadow-elegant">
            <h3 className="font-display font-semibold mb-4">Factores de matching</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {scoring.factors.map((f) => (
                <MatchFactorBar key={f.key} factor={f} />
              ))}
            </div>
          </section>

        </div>

        <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <RecommendationsList items={oportunidad.data.recomendaciones} />

          <section className="rounded-xl border border-border bg-card p-5 shadow-elegant">
            <h3 className="font-display font-semibold mb-4">Empresa vs Licitación</h3>
            {perfil.data ? (
              <CompanyVsTenderTable profile={perfil.data} licitacion={lic} scoring={scoring} />
            ) : (
              <div className="h-32 rounded-lg bg-muted animate-pulse" />
            )}
          </section>
        </div>
      </div>

      {/* Zona de ancho completo: reglas y consola de agentes */}
      <div className="mt-4 space-y-4">
        <RulesPanel reglas={reglasEvaluadas} />

        <AgentConsole
          agentes={analisis.data ?? []}
          enCurso={enCurso}
          errores={errores}
          cargando={analisis.isLoading}
          errorCarga={analisis.isError ? mensajeError(analisis.error) : null}
          onReintentar={() => analisis.refetch()}
          onRun={correrAgente}
          onRunAll={correrTodos}
        />
      </div>

      <DecisionDialog
        pending={pendiente}
        submitting={decidir_.isPending}
        onCancel={() => setPendiente(null)}
        onConfirm={(motivo) =>
          pendiente && decidir_.mutate({ bucket: pendiente.bucket, motivo })
        }
      />
    </AppShell>
  );
}

function DetalleSkeleton() {
  return (
    <AppShell title="Cargando oportunidad…">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-52 rounded-xl bg-muted animate-pulse" />
        ))}
      </div>
      <div className="h-64 rounded-xl bg-muted animate-pulse" />
    </AppShell>
  );
}

function DataRow({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Coins;
  label: string;
  value: string;
  accent?: "destructive" | "warning";
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground flex items-center gap-1.5">
        <Icon className="size-3.5" /> {label}
      </span>
      <span
        className={cn(
          "font-medium text-right",
          accent === "destructive"
            ? "text-destructive"
            : accent === "warning"
              ? "text-warning"
              : "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Tag({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "muted" | "success" | "warning" | "destructive";
}) {
  const cls =
    tone === "success"
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : tone === "destructive"
          ? "text-destructive"
          : "text-foreground";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("text-sm font-semibold", cls)}>{value}</div>
    </div>
  );
}
